import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { 
  Plus, 
  Pencil, 
  Trash2,
  AlertTriangle,
  ArrowUp,
  ArrowDown,
  ShieldCheck
} from 'lucide-react';

interface HardRule {
  id: string;
  company_config_id: string | null;
  rule_order: number;
  rule_title: string;
  rule_content: string;
  enforcement_reason: string | null;
  consequences: string | null;
  is_active: boolean;
  applies_globally: boolean;
  created_at: string;
  updated_at: string;
}

interface CompanyConfig {
  id: string;
  name: string;
}

export default function HardRulesPage() {
  const [rules, setRules] = useState<HardRule[]>([]);
  const [companies, setCompanies] = useState<CompanyConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState<HardRule | null>(null);
  const [formData, setFormData] = useState({
    rule_title: '',
    rule_content: '',
    enforcement_reason: '',
    consequences: '',
    applies_globally: true,
    company_config_id: null as string | null,
    is_active: true
  });

  useEffect(() => {
    fetchRules();
    fetchCompanies();
  }, []);

  const fetchRules = async () => {
    try {
      const { data, error } = await supabase
        .from('hard_rules')
        .select('*')
        .order('rule_order', { ascending: true });

      if (error) throw error;
      setRules(data || []);
    } catch (error) {
      console.error('Error fetching hard rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const { data, error } = await supabase
        .from('company_configs')
        .select('id, name')
        .order('name');

      if (error) throw error;
      setCompanies(data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingRule) {
        // Update existing rule
        const { error } = await supabase
          .from('hard_rules')
          .update({
            rule_title: formData.rule_title,
            rule_content: formData.rule_content,
            enforcement_reason: formData.enforcement_reason,
            consequences: formData.consequences,
            applies_globally: formData.applies_globally,
            company_config_id: formData.applies_globally ? null : formData.company_config_id,
            is_active: formData.is_active,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingRule.id);

        if (error) throw error;
      } else {
        // Create new rule
        const maxOrder = rules.length > 0 ? Math.max(...rules.map(r => r.rule_order)) : 0;
        const { error } = await supabase
          .from('hard_rules')
          .insert({
            rule_order: maxOrder + 1,
            rule_title: formData.rule_title,
            rule_content: formData.rule_content,
            enforcement_reason: formData.enforcement_reason,
            consequences: formData.consequences,
            applies_globally: formData.applies_globally,
            company_config_id: formData.applies_globally ? null : formData.company_config_id,
            is_active: formData.is_active
          });

        if (error) throw error;
      }

      // Reset form and refresh
      setShowForm(false);
      setEditingRule(null);
      resetForm();
      fetchRules();
    } catch (error) {
      console.error('Error saving hard rule:', error);
      alert('Error saving hard rule. Please try again.');
    }
  };

  const handleEdit = (rule: HardRule) => {
    setEditingRule(rule);
    setFormData({
      rule_title: rule.rule_title,
      rule_content: rule.rule_content,
      enforcement_reason: rule.enforcement_reason || '',
      consequences: rule.consequences || '',
      applies_globally: rule.applies_globally,
      company_config_id: rule.company_config_id,
      is_active: rule.is_active
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this hard rule? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('hard_rules')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchRules();
    } catch (error) {
      console.error('Error deleting hard rule:', error);
      alert('Error deleting hard rule. Please try again.');
    }
  };

  const handleToggleActive = async (rule: HardRule) => {
    try {
      const { error } = await supabase
        .from('hard_rules')
        .update({ is_active: !rule.is_active })
        .eq('id', rule.id);

      if (error) throw error;
      fetchRules();
    } catch (error) {
      console.error('Error toggling rule status:', error);
    }
  };

  const handleReorder = async (ruleId: string, direction: 'up' | 'down') => {
    const currentRule = rules.find(r => r.id === ruleId);
    if (!currentRule) return;

    const currentIndex = rules.findIndex(r => r.id === ruleId);
    const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (swapIndex < 0 || swapIndex >= rules.length) return;

    const swapRule = rules[swapIndex];

    try {
      // Swap the orders
      await supabase
        .from('hard_rules')
        .update({ rule_order: swapRule.rule_order })
        .eq('id', currentRule.id);

      await supabase
        .from('hard_rules')
        .update({ rule_order: currentRule.rule_order })
        .eq('id', swapRule.id);

      fetchRules();
    } catch (error) {
      console.error('Error reordering rules:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      rule_title: '',
      rule_content: '',
      enforcement_reason: '',
      consequences: '',
      applies_globally: true,
      company_config_id: null,
      is_active: true
    });
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingRule(null);
    resetForm();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading hard rules...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ShieldCheck className="h-8 w-8 text-red-600" />
              Hard Rules - Non-Negotiable AI Agent Rules
            </h1>
            <p className="text-gray-600 mt-1">
              These are absolute rules that AI agents must follow without exception. They override all other instructions.
            </p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            <Plus className="h-5 w-5" />
            Add Hard Rule
          </button>
        </div>
      </div>

      {/* About Banner */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded p-4">
        <h3 className="text-blue-900 font-semibold">About this page</h3>
        <ul className="mt-2 text-sm text-blue-800 list-disc pl-5 space-y-1">
          <li>Purpose: define non-negotiable, system-level rules that supersede scripts and normal agent rules.</li>
          <li>Data model: stored in <code>hard_rules</code> (fields: rule_order, rule_title, rule_content, enforcement_reason, consequences, applies_globally, company_config_id, is_active).</li>
          <li>Precedence: evaluated before unified <code>ai_agent_rules</code>; keep this list minimal to avoid over-restriction.</li>
          <li>Scope: "Applies to all companies" sets a global rule; otherwise pick a company in the selector to scope locally.</li>
          <li>Priority: reorder with arrows; lower order means higher priority in the prompt.</li>
          <li>Recommendation: for routine behavior and escalation, use <a href="/ai-voice-agents/rules" className="underline">Agent Rules</a> instead.</li>
        </ul>
      </div>

      {/* Warning Banner */}
      <div className="mb-6 bg-red-50 border-l-4 border-red-600 p-4 rounded">
        <div className="flex items-start">
          <AlertTriangle className="h-6 w-6 text-red-600 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-red-800 font-semibold">Critical System Rules</h3>
            <p className="text-red-700 text-sm mt-1">
              These rules are enforced at the system prompt level and cannot be overridden by any other instructions. 
              Use this section only for rules that must NEVER be violated (safety, legal compliance, ethics).
            </p>
          </div>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="mb-6 bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-xl font-bold mb-4">
            {editingRule ? 'Edit Hard Rule' : 'Create New Hard Rule'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rule Title <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.rule_title}
                onChange={(e) => setFormData({ ...formData, rule_title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="e.g., Safety First - Life and Property"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rule Content <span className="text-red-600">*</span>
              </label>
              <textarea
                required
                value={formData.rule_content}
                onChange={(e) => setFormData({ ...formData, rule_content: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Detailed description of what the AI agent must or must not do..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Enforcement Reason
              </label>
              <textarea
                value={formData.enforcement_reason}
                onChange={(e) => setFormData({ ...formData, enforcement_reason: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Why is this rule critical? (Legal requirement, safety, ethics, etc.)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Consequences of Violation
              </label>
              <textarea
                value={formData.consequences}
                onChange={(e) => setFormData({ ...formData, consequences: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="What happens if this rule is violated?"
              />
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.applies_globally}
                  onChange={(e) => setFormData({ ...formData, applies_globally: e.target.checked, company_config_id: e.target.checked ? null : formData.company_config_id })}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Applies to all companies</span>
              </label>

              {!formData.applies_globally && (
                <select
                  value={formData.company_config_id || ''}
                  onChange={(e) => setFormData({ ...formData, company_config_id: e.target.value || null })}
                  className="px-3 py-2 border border-gray-300 rounded-lg"
                  required
                >
                  <option value="">Select Company</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              )}

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Active</span>
              </label>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Rules List */}
      <div className="space-y-4">
        {rules.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <ShieldCheck className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No hard rules defined yet.</p>
            <p className="text-gray-400 text-sm mt-1">
              Click "Add Hard Rule" to create your first non-negotiable AI agent rule.
            </p>
          </div>
        ) : (
          rules.map((rule, index) => (
            <div
              key={rule.id}
              className={`bg-white border-2 rounded-lg p-6 ${
                rule.is_active ? 'border-red-200' : 'border-gray-200 opacity-60'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="flex items-center justify-center w-8 h-8 bg-red-600 text-white rounded-full text-sm font-bold">
                      {rule.rule_order}
                    </span>
                    <h3 className="text-lg font-bold text-gray-900">{rule.rule_title}</h3>
                    {rule.applies_globally && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">Global</span>
                    )}
                    {!rule.is_active && (
                      <span className="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded">Inactive</span>
                    )}
                  </div>

                  <p className="text-gray-700 mb-3 whitespace-pre-wrap">{rule.rule_content}</p>

                  {rule.enforcement_reason && (
                    <div className="mb-2 p-3 bg-yellow-50 rounded border-l-4 border-yellow-400">
                      <p className="text-sm font-semibold text-yellow-900">Why This Matters:</p>
                      <p className="text-sm text-yellow-800">{rule.enforcement_reason}</p>
                    </div>
                  )}

                  {rule.consequences && (
                    <div className="p-3 bg-red-50 rounded border-l-4 border-red-400">
                      <p className="text-sm font-semibold text-red-900">If Violated:</p>
                      <p className="text-sm text-red-800">{rule.consequences}</p>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2 ml-4">
                  <button
                    onClick={() => handleReorder(rule.id, 'up')}
                    disabled={index === 0}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30"
                    title="Move up"
                  >
                    <ArrowUp className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleReorder(rule.id, 'down')}
                    disabled={index === rules.length - 1}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-30"
                    title="Move down"
                  >
                    <ArrowDown className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleToggleActive(rule)}
                    className={`p-2 rounded ${
                      rule.is_active
                        ? 'text-green-600 hover:bg-green-50'
                        : 'text-gray-400 hover:bg-gray-100'
                    }`}
                    title={rule.is_active ? 'Deactivate' : 'Activate'}
                  >
                    <ShieldCheck className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleEdit(rule)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                    title="Edit"
                  >
                    <Pencil className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(rule.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                    title="Delete"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Info Section */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-semibold text-blue-900 mb-2">How Hard Rules Work</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Hard rules are injected into the AI agent's system prompt at the highest priority level</li>
          <li>• These rules override all other instructions, scripts, and guidelines</li>
          <li>• Use hard rules ONLY for non-negotiable requirements (safety, legal, ethical)</li>
          <li>• Rules are enforced in the order listed (lower numbers = higher priority)</li>
          <li>• Global rules apply to all companies; company-specific rules apply only to that company</li>
          <li>• Inactive rules are not sent to AI agents but remain in the database</li>
        </ul>
      </div>
    </div>
  );
}
